import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Download } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import EnquiryCard from "./EnquiryCard";

export const STATUSES = ["new", "contacted", "booked", "declined"];

export default function AdminEnquiries({ onLogout }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/enquiries");
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const replace = (data) => setEnquiries((list) => list.map((e) => (e.id === data.id ? data : e)));

  const updateStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/enquiries/${id}/status`, { status });
      replace(data);
      toast.success(status === "booked" ? "Marked as booked — date reserved on the calendar" : `Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      const { data } = await adminApi.patch(`/enquiries/${id}/notes`, { notes });
      replace(data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const { data, headers } = await adminApi.get("/enquiries/export.csv", {
        params: filter === "all" ? {} : { status: filter },
        responseType: "blob",
      });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "enquiries.csv";
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filter === "all" ? "all" : filter} enquiries`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const counts = useMemo(
    () => STATUSES.reduce((acc, s) => ({ ...acc, [s]: enquiries.filter((e) => e.status === s).length }), {}),
    [enquiries]
  );
  const visible = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);

  return (
    <div data-testid="admin-enquiries-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`border px-4 py-2 text-[10px] font-mono tracking-[0.2em] uppercase transition-colors duration-300 ${
                filter === s ? "border-champagne bg-champagne text-obsidian" : "border-white/15 text-stone-400 hover:border-champagne hover:text-champagne"
              }`}
            >
              {s} <span className="opacity-60">{s === "all" ? enquiries.length : counts[s]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-export-csv-button" onClick={exportCsv} className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-300 hover:border-champagne hover:text-champagne transition-colors">
            <Download size={13} /> Export CSV
          </button>
          <button data-testid="admin-refresh-button" onClick={load} className="border border-white/15 p-3 text-stone-300 hover:border-champagne hover:text-champagne transition-colors" aria-label="Refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && enquiries.length === 0 ? (
        <p data-testid="admin-enquiries-loading" className="mt-16 text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Loading enquiries…</p>
      ) : visible.length === 0 ? (
        <p data-testid="admin-enquiries-empty" className="mt-16 font-serif text-xl italic text-stone-500">No enquiries in this view yet.</p>
      ) : (
        <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
          {visible.map((e) => (
            <EnquiryCard key={e.id} enquiry={e} onStatus={updateStatus} onDelete={remove} onNotes={saveNotes} />
          ))}
        </div>
      )}
    </div>
  );
}
